import html2canvas from 'html2canvas';
import { logger } from './logger';
import { CAR_MODELS, BASE_DIMENSIONS } from '../config/carModels';

// Standard dimensions for preview images
const PREVIEW_DIMENSIONS = {
  width: 1600,    // 1600px width
  height: 900,    // 900px height (16:9 aspect ratio)
  scale: 2        // 2x scaling for higher resolution
};

/**
 * Convert a canvas to a standard aspect ratio by adding padding
 * @param {HTMLCanvasElement} canvas - The original canvas
 * @returns {HTMLCanvasElement} - A new canvas with the standard aspect ratio
 */
const standardizeCanvasAspectRatio = (canvas) => {
  // Create a new canvas with the target dimensions
  const standardCanvas = document.createElement('canvas');
  standardCanvas.width = PREVIEW_DIMENSIONS.width;
  standardCanvas.height = PREVIEW_DIMENSIONS.height;
  
  // Get the context for drawing
  const ctx = standardCanvas.getContext('2d');
  
  // Fill with the appropriate background color - match the car preview background
  ctx.fillStyle = '#212025';
  ctx.fillRect(0, 0, standardCanvas.width, standardCanvas.height);
  
  // Instead of stretching, preserve the original proportions completely
  // and center the car in the frame with padding
  const scale = Math.min(
    standardCanvas.width / canvas.width,
    standardCanvas.height / canvas.height
  );
  
  // Calculate dimensions that preserve the original aspect ratio exactly
  const drawWidth = canvas.width * scale;
  const drawHeight = canvas.height * scale;
  
  // Center the image in the canvas
  const offsetX = (standardCanvas.width - drawWidth) / 2;
  const offsetY = (standardCanvas.height - drawHeight) / 2;
  
  // Draw the original canvas onto the standardized canvas
  ctx.drawImage(canvas, offsetX, offsetY, drawWidth, drawHeight);
  
  return standardCanvas;
};

/**
 * Get the current selected car model and view from the DOM
 * @returns {Object} - { selectedCarModel, selectedView, carDimensions }
 */
const getCurrentCarState = () => {
  // Try to get the selected car model from the car model info element
  const carModelInfo = document.querySelector('.chosen-car-model-info');
  let selectedCarModel = 'volvo-xc90'; // default fallback
  
  if (carModelInfo) {
    const carName = carModelInfo.querySelector('div:last-child')?.textContent;
    // Find the car model by name
    for (const [key, model] of Object.entries(CAR_MODELS)) {
      if (model.name === carName) {
        selectedCarModel = key;
        break;
      }
    }
  }
  
  // Try to get the selected view from the active view button
  const activeViewButton = document.querySelector('.view-selector-btn.active');
  let selectedView = 'Sida Vänster'; // default fallback
  
  if (activeViewButton) {
    selectedView = activeViewButton.textContent.trim();
  }
  
  // Get car dimensions (all models use BASE_DIMENSIONS for canvas)
  const carDimensions = BASE_DIMENSIONS;
  
  console.log('Detected car state:', { selectedCarModel, selectedView, carDimensions });
  
  return { selectedCarModel, selectedView, carDimensions };
};

// Generate preview for a single view
export const generateSingleViewPreview = async (element) => {
  // Store references outside try block for proper cleanup
  let canvasOverlay = null;
  let overlayImgElement = null;
  let overlayParent = null;
  let originalNextSibling = null;
  let carModelInfo = null;
  let controls = null;
  let originalDisplayStyles = {};
  const box = element?.closest('.box-generic-car-preview');
  
  try {
    if (!element) {
      logger.error('No element provided for preview generation');
      return null;
    }

    // Get current car state from DOM
    const { selectedCarModel, selectedView, carDimensions } = getCurrentCarState();

    // Find and temporarily hide UI elements that shouldn't be in the screenshot
    carModelInfo = document.querySelector('.chosen-car-model-info');
    controls = document.querySelector('.design-controls');
    
    // Hide UI elements
    for (const el of [carModelInfo, controls].filter(Boolean)) {
      originalDisplayStyles[el.className] = el.style.display;
      el.style.display = 'none';
    }

    // Store original styles of the element to restore later
    const originalStyles = {
      width: element.style.width,
      height: element.style.height,
      aspectRatio: element.style.aspectRatio,
      padding: element.style.padding
    };
    
    // Store original canvas state to restore later
    let originalCanvasState = null;
    const canvasElement = element.querySelector('canvas');
    if (canvasElement && canvasElement.__fabric) {
      const fabricCanvasInstance = canvasElement.__fabric;
      originalCanvasState = {
        zoom: fabricCanvasInstance.getZoom(),
        width: fabricCanvasInstance.getWidth(),
        height: fabricCanvasInstance.getHeight()
      };
    }
    
    // Force the element to a specific 16:9 aspect ratio regardless of browser window
    const targetWidth = 1600;
    const targetHeight = 900;
    element.style.width = `${targetWidth}px`;
    element.style.height = `${targetHeight}px`;
    element.style.aspectRatio = '16 / 9';
    element.style.padding = '20px';
    
    // Add the screenshot mode class to remove border radius
    element.classList.add('screenshot-mode');
    
    // Also find the parent container and add the class
    if (box) {
      box.classList.add('screenshot-mode');
    }
    
    // Wait for layout to settle after container resize
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Trigger the existing resize mechanism that the regular customizer uses
    // This will automatically handle canvas scaling and car path updates using
    // the exact same logic as the regular customizer
    window.dispatchEvent(new Event('resize'));
    
    // Wait for the resize to be processed
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // Store original SVG styles (but don't modify SVG - let it use CSS object-contain)
    const svgOverlay = element.querySelector('.car-preview-overlay');
    let originalSvgStyles = {};
    
    if (svgOverlay) {
      // Just store original styles, don't modify the SVG positioning
      originalSvgStyles = {
        objectFit: svgOverlay.style.objectFit,
        objectPosition: svgOverlay.style.objectPosition,
        transform: svgOverlay.style.transform,
        transformOrigin: svgOverlay.style.transformOrigin,
        left: svgOverlay.style.left,
        top: svgOverlay.style.top,
        position: svgOverlay.style.position
      };
      
      console.log('SVG overlay will use regular CSS object-contain positioning');
    }
    
    // Store canvas overlay reference to restore it later
    canvasOverlay = element.querySelector('canvas.car-preview-overlay');
    
    // Clean up any stray img elements from previous captures
    const strayImgs = element.querySelectorAll('img.car-preview-overlay');
    strayImgs.forEach(img => {
      console.warn('Removing stray img overlay from previous capture');
      img.remove();
    });
    
    // Temporarily replace canvas overlay with img tag for html2canvas
    if (canvasOverlay) {
      try {
        // Create an img element from the canvas
        const overlayDataUrl = canvasOverlay.toDataURL('image/png');
        overlayImgElement = document.createElement('img');
        overlayImgElement.src = overlayDataUrl;
        overlayImgElement.className = canvasOverlay.className;
        overlayImgElement.style.cssText = canvasOverlay.style.cssText;
        overlayImgElement.style.visibility = 'visible';
        
        // Store parent and position for restoration
        overlayParent = canvasOverlay.parentNode;
        originalNextSibling = canvasOverlay.nextSibling;
        
        // Replace canvas with img
        overlayParent.replaceChild(overlayImgElement, canvasOverlay);
        
        console.log('Temporarily replaced canvas overlay with img for capture');
      } catch (err) {
        console.warn('Could not convert canvas overlay to img:', err);
      }
    }
    
    // Capture the screenshot with fixed dimensions
    const originalCanvas = await html2canvas(element, {
      width: targetWidth,
      height: targetHeight,
      scale: 1, // Use scale 1 since we're already at target resolution
      useCORS: false, // Disabled - all images are now hosted locally
      allowTaint: true, // Allow local images without CORS restrictions
      backgroundColor: '#EBEBEB', // Plain hex color for html2canvas compatibility
      logging: false, // Disable logging for production
      // Force specific window dimensions for consistent capture
      windowWidth: targetWidth + 100, // Add some padding
      windowHeight: targetHeight + 100,
      onclone: (clonedDoc) => {
        const clonedElement = clonedDoc.querySelector('.car-preview-canvas-container');
        if (clonedElement) {
          clonedElement.classList.add('screenshot-mode');
          clonedElement.style.width = `${targetWidth}px`;
          clonedElement.style.height = `${targetHeight}px`;
          clonedElement.style.aspectRatio = '16 / 9';
          clonedElement.style.backgroundColor = '#EBEBEB';
          
          const clonedBox = clonedElement.closest('.box-generic-car-preview');
          if (clonedBox) {
            clonedBox.classList.add('screenshot-mode');
            clonedBox.style.backgroundColor = '#EBEBEB';
          }
          
          // Hide UI elements (shadows, buttons, tooltips, help circle)
          const uiElements = clonedDoc.querySelectorAll('button, .tooltip-graphic-info, .keyboard-shortcuts-trigger, [class*="shadow"]');
          uiElements.forEach(el => {
            el.style.display = 'none';
            el.style.opacity = '0';
            el.style.visibility = 'hidden';
          });
          
          console.log('Applied basic styling to cloned document elements');
        }
      }
    });
    
    // Log the captured canvas dimensions for debugging
    console.log(`Preview captured: ${originalCanvas.width}x${originalCanvas.height} (aspect ratio: ${(originalCanvas.width / originalCanvas.height).toFixed(3)})`);
    
    // The canvas should now be exactly the right size and aspect ratio
    const dataURL = originalCanvas.toDataURL('image/png');

    // Restore all original styles
    Object.keys(originalStyles).forEach(key => {
      if (originalStyles[key] !== undefined) {
        element.style[key] = originalStyles[key];
      } else {
        element.style.removeProperty(key.replace(/([A-Z])/g, '-$1').toLowerCase());
      }
    });
    
    // Restore original canvas state
    if (originalCanvasState && canvasElement && canvasElement.__fabric) {
      const fabricCanvasInstance = canvasElement.__fabric;
      fabricCanvasInstance.setZoom(originalCanvasState.zoom);
      fabricCanvasInstance.setDimensions({
        width: originalCanvasState.width,
        height: originalCanvasState.height
      });
      fabricCanvasInstance.renderAll();
      
      console.log('Restored original canvas state:', originalCanvasState);
    }
    
    // Restore SVG overlay styles
    if (svgOverlay && Object.keys(originalSvgStyles).length > 0) {
      Object.keys(originalSvgStyles).forEach(key => {
        if (originalSvgStyles[key] !== undefined && originalSvgStyles[key] !== null && originalSvgStyles[key] !== '') {
          svgOverlay.style[key] = originalSvgStyles[key];
        } else {
          svgOverlay.style.removeProperty(key.replace(/([A-Z])/g, '-$1').toLowerCase());
        }
      });
    }
    
    // Remove the screenshot mode class
    element.classList.remove('screenshot-mode');
    if (box) {
      box.classList.remove('screenshot-mode');
    }

    return dataURL;
  } catch (error) {
    logger.error('Error generating preview:', error);
    return null;
  } finally {
    // Always restore canvas overlay if it was replaced
    if (canvasOverlay && overlayImgElement && overlayParent) {
      try {
        // Replace the img back with the original canvas
        if (overlayImgElement.parentNode === overlayParent) {
          overlayParent.replaceChild(canvasOverlay, overlayImgElement);
          console.log('Restored canvas overlay by replacing img');
        } else {
          // Fallback: just append the canvas back
          if (originalNextSibling && originalNextSibling.parentNode === overlayParent) {
            overlayParent.insertBefore(canvasOverlay, originalNextSibling);
          } else {
            overlayParent.appendChild(canvasOverlay);
          }
          console.log('Restored canvas overlay by inserting');
        }
      } catch (err) {
        console.warn('Could not restore canvas overlay in finally:', err);
      }
    }
  }
};

// Generate previews for all views
export const generateAllViewsPreviews = async (currentView, setSelectedView) => {
  const views = ['Sida Vänster', 'Sida Höger', 'Fram', 'Bak', 'Topp'];
  const previews = {};
  
  // Store original view to restore later
  const originalView = currentView;
  
  console.log('Starting to generate all view previews...');
  
  // For each view
  for (const view of views) {
    console.log(`Generating preview for ${view}...`);
    
    // Change to this view
    setSelectedView(view);
    
    // Wait for the view to render - reduced delay for faster capture
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Capture the preview
    const element = document.querySelector('.car-preview-canvas-container');
    if (element) {
      const preview = await generateSingleViewPreview(element);
      if (preview) {
        previews[view] = preview;
        console.log(`✓ Generated preview for ${view}`);
      } else {
        console.warn(`✗ Failed to generate preview for ${view}`);
      }
      
      // Small delay after capture to ensure DOM is fully restored
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  // Restore original view
  setSelectedView(originalView);
  
  // Wait for the original view to be restored
  await new Promise(resolve => setTimeout(resolve, 200));
  
  console.log('All view previews generated:', Object.keys(previews));
  
  return previews;
};

// Legacy function for backward compatibility
export const generateDesignPreview = async () => {
  const element = document.querySelector('.car-preview-canvas-container');
  return generateSingleViewPreview(element);
};

/**
 * Debug function to apply screenshot mode styling without taking a screenshot
 * This allows debugging the alignment in the browser
 */
export const enableScreenshotDebugMode = async () => {
  const element = document.querySelector('.car-preview-canvas-container');
  if (!element) {
    console.error('No car preview container found');
    return;
  }

  // Get current car state from DOM
  const { selectedCarModel, selectedView, carDimensions } = getCurrentCarState();
  
  console.log('Enabling screenshot debug mode with:', { selectedCarModel, selectedView });

  // Hide UI elements
  const carModelInfo = document.querySelector('.chosen-car-model-info');
  const controls = document.querySelector('.design-controls');
  
  if (carModelInfo) carModelInfo.style.display = 'none';
  if (controls) controls.style.display = 'none';

  // Force the element to a specific 16:9 aspect ratio
  const targetWidth = 1600;
  const targetHeight = 900;
  element.style.width = `${targetWidth}px`;
  element.style.height = `${targetHeight}px`;
  element.style.aspectRatio = '16 / 9';
  element.style.padding = '20px';
  
  // Add the screenshot mode class
  element.classList.add('screenshot-mode');
  
  const box = element.closest('.box-generic-car-preview');
  if (box) {
    box.classList.add('screenshot-mode');
  }
  
  // Wait for layout to settle after container resize
  await new Promise(resolve => setTimeout(resolve, 100));
  
  // Trigger the existing resize mechanism that the regular customizer uses
  // This will automatically handle canvas scaling and car path updates
  window.dispatchEvent(new Event('resize'));
  
  // Wait for the resize to be processed
  await new Promise(resolve => setTimeout(resolve, 200));
  
  console.log('Screenshot debug mode enabled!');
  console.log('The regular customizer resize logic has been triggered automatically.');
  console.log('Canvas and SVG should now be aligned using the exact same logic as the regular customizer.');
  console.log('To disable, call disableScreenshotDebugMode()');
};

/**
 * Disable screenshot debug mode and restore normal view
 */
export const disableScreenshotDebugMode = () => {
  const element = document.querySelector('.car-preview-canvas-container');
  if (!element) return;
  
  // Show UI elements
  const carModelInfo = document.querySelector('.chosen-car-model-info');
  const controls = document.querySelector('.design-controls');
  
  if (carModelInfo) carModelInfo.style.display = '';
  if (controls) controls.style.display = '';
  
  // Remove screenshot mode styles
  element.style.removeProperty('width');
  element.style.removeProperty('height');
  element.style.removeProperty('aspect-ratio');
  element.style.removeProperty('padding');
  
  element.classList.remove('screenshot-mode');
  
  const box = element.closest('.box-generic-car-preview');
  if (box) {
    box.classList.remove('screenshot-mode');
  }
  
  // Restore SVG overlay
  const svgOverlay = element.querySelector('.car-preview-overlay');
  if (svgOverlay) {
    svgOverlay.style.removeProperty('position');
    svgOverlay.style.removeProperty('left');
    svgOverlay.style.removeProperty('top');
    svgOverlay.style.removeProperty('object-fit');
    svgOverlay.style.removeProperty('object-position');
    svgOverlay.style.removeProperty('transform');
    svgOverlay.style.removeProperty('transform-origin');
  }
  
  // Restore canvas - trigger normal resize
  const canvasElement = element.querySelector('canvas');
  if (canvasElement && canvasElement.__fabric) {
    // Trigger the normal resize logic by dispatching a resize event
    window.dispatchEvent(new Event('resize'));
  }
  
  console.log('Screenshot debug mode disabled');
};

// Make these functions available globally for easy debugging
if (typeof window !== 'undefined') {
  window.enableScreenshotDebugMode = enableScreenshotDebugMode;
  window.disableScreenshotDebugMode = disableScreenshotDebugMode;
} 